import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin, Button, Empty } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import axiosInstance from '../../variables/api/axiosInstance';
import { HAB, can } from '../../variables/constants';
import logoCarteBrune from '../../assets/images/logo.png';
import { IndicesReedition, markPrinted, wasPrinted } from './indiceReedition';
import { useReglagesIndices } from './useReglagesIndices';
import './attestations.css';
import BlocSignature from '../../components/BlocSignature';
import { useAvertissementSignature } from '../../components/avertissementSignature';
import CaseApposerSignature from '../../components/CaseApposerSignature';

/* ─────────────────────────────────────────────────────────────
   ATTESTATION DE DÉCLARATION D'ACCIDENT - BNCB
   Émis après confirmation de garantie acquise (statut GARANTIE_CONFIRMEE).
   Route : /sinistres/:id/attestation

   Pièce remise au guichet, en français quelle que soit la langue de l'écran : ses
   dates sont formatées en français localement, et seul l'écran autour (boutons,
   messages) suit la langue de l'agent. Pour un homologue anglophone, c'est la
   version PDF classée dans la GED et jointe à la demande de garantie qui porte la
   traduction en regard (AttestationDeclarationPdf, côté serveur) : celle-ci doit
   tenir sur une seule page A4.
   ───────────────────────────────────────────────────────────── */
export default function AttestationDeclaration() {

  /* Les signatures sont temporaires : celle qui valait a l'ouverture de cet
     ecran peut avoir expire. L'etat est relu au clic, et l'agent est averti que
     la feuille sortira vierge - puis libre a lui d'imprimer. */
  const { confirmerPuisImprimer } = useAvertissementSignature('ATTESTATION_DECLARATION');
  /* Le paramétrage dit qu'un document est signé ; il ne peut pas savoir que CE
     tirage-là n'en veut pas. Le responsable garde le dernier mot. */
  const [apposer, setApposer] = useState(true);
  const { t } = useTranslation('documents');
  const { id } = useParams();
  const reglagesIndices = useReglagesIndices();
  const navigate = useNavigate();
  const [sinistre, setSinistre] = useState(null);
  const [assure, setAssure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [archivage, setArchivage] = useState(false);

  // L'attestation est classée automatiquement à la confirmation de la garantie. Ce
  // bouton sert aux dossiers confirmés avant, ou à réémettre après une correction :
  // chaque archivage ajoute un document, il ne remplace pas celui déjà remis.
  const classerDansLaGed = async () => {
    setArchivage(true);
    try {
      const res = await axiosInstance.post(`/v1/sinistres/${id}/attestation-declaration/archiver`);
      toast.success(res.data?.message || t('ecran.attestationDeclaration.classee'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ecran.attestationDeclaration.nonClassee'));
    } finally {
      setArchivage(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sRes = await axiosInstance.get(`/v1/sinistres/${id}`);
      const s = sRes.data?.data;
      setSinistre(s);
      if (s?.assureTrackingId) {
        try {
          const aRes = await axiosInstance.get(`/v1/assures/${s.assureTrackingId}`);
          setAssure(aRes.data?.data);
        } catch { /* ignore */ }
      }
    } catch {
      setSinistre(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="att-page"><Spin /></div>;
  if (!sinistre) {
    return (
      <div className="att-page">
        <Empty description={t('ecran.sinistreIntrouvable')} />
        <Button onClick={() => navigate('/sinistres')} icon={<ArrowLeftOutlined />} style={{ marginTop: 12 }}>
          {t('ecran.retour')}
        </Button>
      </div>
    );
  }

  const fmtDateLong = (d) => d ? dayjs(d).locale('fr').format('DD MMMM YYYY').toUpperCase() : '-';
  const fmtDateShort = (d) => d ? dayjs(d).format('DD/MM/YYYY') : '-';
  const fmtMarque = (v) => {
    if (!v) return '-';
    if (!v.includes('.')) return v;
    const raw = v.split('.').pop().replace(/_/g, ' ');
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  };

  /* Code initiales = initiales de chaque partie du nom/prénom du rédacteur.
     Ex: "AMELETO ELI" → "AE", "Kokou Edem AZIABLEMOU" → "KEA" */
  const computeInitiales = (nomPrenom) => {
    if (!nomPrenom) return '';
    return nomPrenom
      .split(/[\s-]+/)
      .filter(Boolean)
      .map(w => w[0])
      .join('')
      .toUpperCase();
  };
  const codeInitiales = computeInitiales(sinistre.redacteurNomPrenom);

  const conducteurNom = sinistre.conducteurEstAssure
    ? (assure?.nomComplet || sinistre.assureNomComplet || '-')
    : [sinistre.conducteurNom, sinistre.conducteurPrenom].filter(Boolean).join(' ');

  const permisLieu = [sinistre.conducteurNumeroPermis, sinistre.conducteurLieuDelivrance]
    .filter(Boolean).join(' A ');

  const lieuAccident = sinistre.ville
    ? `${fmtDateLong(sinistre.dateAccident)} A ${sinistre.ville.toUpperCase()}`
    : fmtDateLong(sinistre.dateAccident);

  const dateFaite = sinistre.dateConfirmationGarantie || dayjs().format('YYYY-MM-DD');
  const dejaImprime = wasPrinted('attestation', id);

  return (
    <div className="att-page">
      <div className="att-toolbar no-print">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/sinistres/${id}`)}>
          {t('ecran.retourDossier')}
        </Button>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {sinistre.garantieAcquise === true && can(HAB.GED_CREATE) && (
            <Button icon={<CloudUploadOutlined />} loading={archivage} onClick={classerDansLaGed}>
              {t('ecran.attestationDeclaration.classer')}
            </Button>
          )}
          <CaseApposerSignature codeDocument="ATTESTATION_DECLARATION" valeur={apposer} onChange={setApposer} />
          <Button type="primary" icon={<PrinterOutlined />} onClick={() => confirmerPuisImprimer(() => { markPrinted('attestation', id); window.print(); })} style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
            {t('ecran.imprimer')}
          </Button>
        </div>
      </div>

      <div className="att-sheet">
        <div style={{ position: 'absolute', top: '8mm', right: '12mm', zIndex: 2 }}>
          <IndicesReedition
            dejaModifie={!!sinistre?.dejaModifie}
            dejaImprime={dejaImprime}
            reglages={reglagesIndices}
          />
        </div>
        <style>{`
    .att-sheet *:not(.att-title):not(.att-subtitle) {
      font-size: 11px !important;
      line-height: 1.35 !important;
    }
  `}</style>
        {/*  Entête bilingue  */}
        <div className="att-header">
          <div className="att-header-left">
            BUREAU NATIONAL TOGOLAIS<br />
            SYSTÈME CARTE BRUNE CEDEAO
          </div>
          <img src={logoCarteBrune} alt="Carte Brune CEDEAO" className="att-logo-img" />
          <div className="att-header-right">
            TOGO NATIONAL BUREAU<br />
            ECOWAS BROWN CARD
          </div>
        </div>

        {/*  Titre  */}
        <div className="att-title-wrap">
          <div className="att-title-block">
            <h1 className="att-title">Attestation</h1>
            <div className="att-subtitle">De déclaration d'accident</div>
            <div className="att-sin-line">
              <span className="sin-label">SIN. </span>
              <span className="sin-value">
                {(sinistre.numeroSinistreLocal || '-') + ' DU ' + fmtDateShort(sinistre.dateDeclaration)}
              </span>
            </div>
          </div>
        </div>

        {/*  Encadré IMPORTANT  */}
        <div className="att-important">
          <u>IMPORTANT</u> : Prière communiquer les contacts de la ou les victimes ou leur demander
          de contacter le Bureau National Togolais au téléphone pour toute réclamation.
          <div className="center">Appelez : 00 (228) 22 25 82 32 / 93 00 23 32 / 97 03 23 32</div>
        </div>

        {/*  Accroche  */}
        <div className="att-accroche">
          Nous soussignés, Bureau National Togolais de la Carte Brune CEDEAO, attestons avoir
          enregistré la présente déclaration d'accident suivant les informations ci-après :
        </div>

        {/*  Tableau d'informations  */}
        <table className="att-info-table">
          <tbody>
            <tr>
              <td className="att-label">
                DATE ET LIEU DE L'ACCIDENT <em>/ Date and location of accident</em>
              </td>
              <td className="att-value">{lieuAccident}</td>
            </tr>
            <tr>
              <td className="att-label">NOM DE L'ASSURÉ <em>/ Insured</em></td>
              <td className="att-value">{(assure?.nomComplet || sinistre.assureNomComplet || '-').toUpperCase()}</td>
            </tr>
            <tr>
              <td className="att-label">ASSUREUR <em>/ Insurance company</em></td>
              <td className="att-value">{(sinistre.organismeMembreRaisonSociale || '-').toUpperCase()}</td>
            </tr>
            <tr>
              <td className="att-label">MARQUE DU VÉHICULE IMPLIQUÉ <em>/ Make</em></td>
              <td className="att-value">{fmtMarque(assure?.marqueVehicule).toUpperCase()}</td>
            </tr>
            <tr>
              <td className="att-label">IMMATRICULATION <em>/ Registration</em></td>
              <td className="att-value mono">{(assure?.immatriculation || '-').toUpperCase()}</td>
            </tr>
            <tr>
              <td className="att-label">CONDUCTEUR <em>/ Driver</em></td>
              <td className="att-value">{(conducteurNom || '-').toUpperCase()}</td>
            </tr>
            <tr>
              <td className="att-label">
                N° ET LIEU DE DÉLIVRANCE DU PERMIS <em>/ Driving license</em>
              </td>
              <td className="att-value">{permisLieu ? permisLieu.toUpperCase() : '-'}</td>
            </tr>
            <tr>
              <td className="att-label">
                NOM ET PRÉNOMS DU DÉCLARANT <em>/ Name of declarant</em>
              </td>
              <td className="att-value">
                {[sinistre.declarantNom, sinistre.declarantPrenom].filter(Boolean).join(' ').toUpperCase() || '-'}
              </td>
            </tr>
            <tr>
              <td className="att-label">ADRESSE DU DÉCLARANT <em>/ Address</em></td>
              <td className="att-value">{sinistre.declarantTelephone || '-'}</td>
            </tr>
            <tr>
              <td className="att-label">RÉFÉRENCE SINISTRE <em>/ Our reference</em></td>
              <td className="att-value">
                <div className="att-value-with-code">
                  <span className="mono">{sinistre.numeroSinistreLocal || '-'}</span>
                  <span className="att-initiales">{codeInitiales || '-'}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/*  Fermeture  */}
        <div className="att-closing">
          En foi de quoi, nous délivrons la présente attestation pour servir et valoir ce que
          de droit.
        </div>

        <div className="att-nb">
          <strong>NB</strong> : Nous vous prions de bien vouloir nous faire tenir le rapport de constat
          d'accident ou le procès-verbal de l'accident avec le croquis d'état des lieux en y
          indiquant les contacts des victimes.
        </div>

        <div className="att-fait-a">
          Fait à Lomé : <strong>{fmtDateLong(dateFaite)}</strong>
        </div>
        <div className="att-pour-bncb">Pour le Bureau National</div>
        <div className="att-signature-espace" />
        <div className="att-pour-bncb">Le Secrétaire Exécutif</div>
        {/* La signature se loge DANS l'espace déjà réservé, elle ne s'y ajoute pas :
            c'est ce qui laisse le document sur une seule page A4. */}
        <div className="att-signature-espace">
          <BlocSignature
            codeDocument="ATTESTATION_DECLARATION"
            actif={apposer}
            sinistreTrackingId={sinistre.sinistreTrackingId}
          />
        </div>

        {/*  Footer  */}
        <div className="att-footer">
          290 Rue AGP Agbalepedo · 01 BP 2689 Lomé-TOGO · Tél (+228) 22 25 82 32 · Gsm (+228) 93 00 23 32<br />
          E-mail : tgocartebrune@cartebrune.org
        </div>
      </div>
    </div>
  );
}
